import { Box, ButtonBase } from '@mui/material'
import React from 'react'
import {
  AGE_ENTRY_MODE_AGE,
  AGE_ENTRY_MODE_DOB,
  formatPatientAgeSummary,
  getNormalizedAgeEntryMode,
  getTodayDateInputValue,
  sanitizeDateOfBirthInput,
} from '../utils/patientAgeUtils'
import { FORM_CONSTANTS } from '../utils/patientFormConfig'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import {
  PATIENT_FORM_TOKENS as T,
  PatientFormField,
  PatientNativeSelect,
  PatientTextInput,
} from './PatientFormPrimitives'

function preventInvalidAgeKeyDown(event) {
  if (['.', ',', '-', '+', 'e', 'E'].includes(event.key)) {
    event.preventDefault()
  }
}

/** Tiny "Age | Date of birth" switch that sits on the field label line. */
function AgeModeSwitch({ mode, onChange, disabled, labels }) {
  const options = [
    { id: AGE_ENTRY_MODE_AGE, label: labels.age },
    { id: AGE_ENTRY_MODE_DOB, label: labels.dob },
  ]
  return (
    <Box
      role="radiogroup"
      aria-label={labels.group}
      sx={{
        display: 'inline-flex',
        border: '1px solid #e2e8f0',
        borderRadius: '999px',
        padding: '2px',
        background: T.surfaceSoft,
        flexShrink: 0,
      }}
    >
      {options.map((option) => {
        const selected = option.id === mode
        return (
          <ButtonBase
            key={option.id}
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => {
              if (!disabled && !selected) onChange(option.id)
            }}
            sx={{
              padding: '2px 9px',
              borderRadius: '999px',
              fontSize: '11.5px',
              fontWeight: 600,
              lineHeight: 1.4,
              color: selected ? '#312e81' : T.muted,
              background: selected ? '#e0e7ff' : 'transparent',
              transition: 'background 0.15s ease, color 0.15s ease',
              '&.Mui-focusVisible': { boxShadow: `0 0 0 2px ${T.accentRing}` },
            }}
          >
            {option.label}
          </ButtonBase>
        )
      })}
    </Box>
  )
}

/**
 * Age or Date of birth, as one field. Age mode is one 40px row (years, months/days, unit);
 * DOB mode is a native date picker with the computed age shown beneath. Whichever mode is
 * used, the derived age fields stay in sync so `age` readers keep working, while
 * `dobProvided` is only sent when an actual date was entered.
 */
export default function PatientAgeField({
  patientDetails,
  editMode = true,
  formHook,
  validationHook,
  span = { xs: 12, md: 6 },
  requiredEmpty = false,
}) {
  const { copy } = usePatientFormI18n()
  const ageValidation = validationHook.getFieldValidation('age')
  const secondaryUnit = patientDetails?.ageSecondaryUnit || FORM_CONSTANTS.DEFAULT_AGE_SECONDARY_UNIT
  const mode = getNormalizedAgeEntryMode(patientDetails?.ageEntryMode)
  const isDobMode = mode === AGE_ENTRY_MODE_DOB
  const invalid = ageValidation.showError
  const dobValue = patientDetails?.dobProvided || ''
  const derivedAgeSummary =
    isDobMode && sanitizeDateOfBirthInput(dobValue) ? formatPatientAgeSummary(patientDetails) : null

  return (
    <PatientFormField
      label={isDobMode ? copy.dateOfBirthLabel : copy.ageLabel}
      required
      span={span}
      htmlFor={isDobMode ? 'patient-dob' : 'patient-age-years'}
      error={ageValidation.showError ? ageValidation.errorMessage : null}
      hint={derivedAgeSummary ? `${copy.ageFromDobPrefix}: ${derivedAgeSummary}` : null}
      hintTone="success"
      requiredEmpty={requiredEmpty}
      labelAddon={
        <AgeModeSwitch
          mode={mode}
          disabled={!editMode}
          onChange={formHook.handleAgeEntryModeChange}
          labels={{ age: copy.ageModeAgeLabel, dob: copy.ageModeDobLabel, group: copy.ageLabel }}
        />
      }
    >
      {isDobMode ? (
        <PatientTextInput
          id="patient-dob"
          type="date"
          max={getTodayDateInputValue()}
          aria-label={copy.dateOfBirthLabel}
          value={dobValue}
          onChange={formHook.handleDobProvidedChange}
          onBlur={() => validationHook.markTouched?.('age')}
          disabled={!editMode}
          invalid={invalid}
          sx={{ '&::-webkit-calendar-picker-indicator': { cursor: 'pointer', opacity: 0.7 } }}
        />
      ) : (
        <Box sx={{ display: 'flex', gap: '8px', width: '100%', minWidth: 0 }}>
          <PatientTextInput
            id="patient-age-years"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={copy.ageYearsLabel}
            value={patientDetails?.ageYears ?? patientDetails?.age ?? ''}
            onChange={formHook.handleAgeChange}
            onBlur={() => validationHook.markTouched?.('age')}
            onKeyDown={preventInvalidAgeKeyDown}
            disabled={!editMode}
            placeholder={copy.ageYearsPlaceholder}
            invalid={invalid}
            sx={{ flex: '1.15 1 0' }}
          />
          <PatientTextInput
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={copy.ageSecondaryValueLabel}
            value={patientDetails?.ageSecondaryValue ?? ''}
            onChange={formHook.handleAgeSecondaryValueChange}
            onBlur={() => validationHook.markTouched?.('age')}
            onKeyDown={preventInvalidAgeKeyDown}
            disabled={!editMode}
            placeholder={
              secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
                ? copy.ageSecondaryDaysPlaceholder
                : copy.ageSecondaryPlaceholder
            }
            invalid={invalid}
            sx={{ flex: '1 1 0' }}
          />
          <Box sx={{ flex: '0 0 auto', width: { xs: '100px', md: '108px' } }}>
            <PatientNativeSelect
              aria-label={copy.ageSecondaryUnitLabel}
              value={secondaryUnit}
              onChange={formHook.handleAgeSecondaryUnitChange}
              disabled={!editMode}
              options={[
                { value: FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS, label: copy.ageUnitMonths },
                { value: FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS, label: copy.ageUnitDays },
              ]}
            />
          </Box>
        </Box>
      )}
    </PatientFormField>
  )
}
